import { useMemo } from 'react';
import { Box, Paper, TextField } from '@mui/material';
import type { RockUCSNSGeneral, RockUCSNSSpecimen } from '../../../types/rockUcsNs';
import { computeUcsNsSpecimenPreview } from './computations';
import { inputSx, paperSx, SectionHeader } from '../rock-tcs/sectionStyles';
import { SpecimenResultCard } from './sections/SpecimenResultCard';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

interface Props {
  general: RockUCSNSGeneral;
  /** The single specimen (specimen 1 — BUG-2026-0138 item 4). */
  specimen: RockUCSNSSpecimen;
  onGeneralChange: (field: keyof RockUCSNSGeneral, value: RockUCSNSGeneral[keyof RockUCSNSGeneral]) => void;
  onSpecimenChange: (field: keyof RockUCSNSSpecimen, value: RockUCSNSSpecimen[keyof RockUCSNSSpecimen]) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

/**
 * Tab 2 — Result Entry (Rock UCS No Strain / ASTM D7012 Method C):
 * one Maximum Load (kN) → derived UCS (MPa), test duration (seconds AND
 * decimal minutes — BUG-2026-0138 item 8), derived stress rate, and the full
 * TCS-style failure-mode block, plus the report remarks box.
 *
 * BUG-2026-0138 item 4: the test always has exactly ONE specimen — the
 * multi-specimen card grid and Summary-of-Results strip are gone.
 */
export const RockUCSNSResultEntryTab = ({
  general,
  specimen,
  onGeneralChange,
  onSpecimenChange,
  readOnly,
  workOrderDetailKey,
}: Props) => {
  const preview = useMemo(() => computeUcsNsSpecimenPreview(specimen), [specimen]);

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5 }}>
      {/* ── Result card (load / UCS / duration / failure mode) ───────────── */}
      <SpecimenResultCard
        specimen={specimen}
        preview={preview}
        title="Test Results"
        onChange={onSpecimenChange}
        readOnly={readOnly}
      />

      {/* ── Report Remarks ───────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Report Remarks (appended to Laboratory Comments)" />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          placeholder="Optional remarks printed after the failure description in the report's LABORATORY COMMENTS box."
          value={general.remarksText ?? ''}
          onChange={(e) => onGeneralChange('remarksText', e.target.value || null)}
          InputProps={{ readOnly }}
          sx={inputSx}
        />
      </Paper>

      {/* ── Internal media evidence — never printed ─────────────────────── */}
      {workOrderDetailKey && (
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="Additional Observations (internal — not printed on the report)" />
          <TextField
            size="small" fullWidth multiline minRows={3}
            placeholder="Internal observations about specimen, test conditions, or equipment — for the lab and reviewer only."
            value={general.additionalObservations ?? ''}
            onChange={(e) => onGeneralChange('additionalObservations', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        </Paper>
      )}
    </Box>
  );
};
